import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Center,
  IconButton,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Divider,
  Button
} from '@chakra-ui/react';
import { CloseIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { FiAlertTriangle } from 'react-icons/fi';
import { useDrag, useDrop } from 'react-dnd';
import { useTeamLineup, useDepthChart, DepthChartType, PlayerType, PHEntry, PHSituations } from '../hooks/useLineups';
import { useUserContext } from '../contexts/UserContext';

const darkBg = '#181A20';
const darkTableBg = '#23262F';
const darkText = 'white';
const darkSecondary = 'gray.400';

const DEPTH_POSITIONS = ['CA', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH', 'PR'] as const;
const DEPTH_COLUMNS = ['1st', '2nd', '3rd', '4th'];
const DEPTH_CELL_WIDTH = '180px';

const PH_SITUATIONS = [
  { key: 'any' as const, label: 'Any situation' },
  { key: 'baserunner' as const, label: 'Need baserunner' },
  { key: 'rbi' as const, label: 'Need RBI' },
  { key: 'last' as const, label: 'Last choice' }
];

const PH_PILL_COLORS: Record<PHSituations, string> = {
  any: '#DD6B20',
  baserunner: '#3182CE',
  rbi: '#1A202C',
  last: '#4A5568',
  unset: '#4A5568'
};

function useDebouncedCallback<T extends (...args: any[]) => void>(
  callback: T,
  delay: number
): T {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastArgsRef = useRef<Parameters<T>>();

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncedFn = useCallback(
    ((...args: Parameters<T>) => {
      lastArgsRef.current = args;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...lastArgsRef.current!);
        timeoutRef.current = null;
      }, delay);
    }) as T,
    [delay]
  );

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        if (lastArgsRef.current) {
          callbackRef.current(...lastArgsRef.current);
        }
      }
    },
    []
  );
  return debouncedFn;
}

const ItemTypes = {
  DEPTH_PLAYER: 'depth_player',
  BENCH_PLAYER: 'bench_player'
};

type DepthDragItem = {
  rosterPlayerId: number;
  source: 'depth';
  fromPosition: string;
  fromSlotIndex: number;
};

type BenchDragItem = {
  rosterPlayerId: number;
  source: 'bench';
};

type DragItem = DepthDragItem | BenchDragItem;

type PlayerWithId = PlayerType & {
  roster_player_id?: number;
  firstName?: string;
  lastName?: string;
  positions?: string[];
  primaryPosition?: string[];
  secondaryPosition?: string[];
  tertiaryPosition?: string[];
};

function getPlayerName(p: PlayerWithId | undefined): string {
  if (!p) return '';
  if (p.firstName && p.lastName) return `${p.firstName} ${p.lastName}`;
  if (p.full_name) return p.full_name;
  if (p.name) return p.name;
  return '';
}

function getPlayerPositionsArray(p: PlayerWithId | undefined): string[] {
  if (!p) return [];
  if (Array.isArray(p.positions) && p.positions.length > 0) return p.positions;
  return [].concat(
    p.primaryPosition || [],
    p.secondaryPosition || [],
    p.tertiaryPosition || []
  ).filter(Boolean) as string[];
}

type PositionWarningLevel = 'primary' | 'secondary' | 'tertiary' | 'out';

function getPositionWarningLevel(player: PlayerWithId | undefined, depthPosition: string): PositionWarningLevel {
  if (!player) return 'primary';
  if (['DH', 'PR', 'PH'].includes(depthPosition)) return 'primary';

  const positionOptions = POSITION_OPTIONS[depthPosition];
  const hasPrimary = player.primaryPosition?.some(a => positionOptions.includes(a));
  if (hasPrimary) return 'primary';

  const hasSecondary = player.secondaryPosition?.some(a => positionOptions.includes(a));
  if (hasSecondary) return 'secondary';

  const hasTertiary = player.tertiaryPosition?.some(a => positionOptions.includes(a));
  if (hasTertiary) return 'tertiary';

  return 'out';
}

type DepthCellProps = {
  position: string;
  slotIndex: number;
  rosterPlayerId: number | undefined;
  playerMap: Map<number, PlayerWithId>;
  onDrop: (position: string, slotIndex: number, item: DragItem) => void;
  onRemove: (position: string, slotIndex: number) => void;
  onReorder: (position: string, fromIdx: number, toIdx: number) => void;
  depthIds: number[];
  onPlayerClick: (player: PlayerWithId) => void;
  onEmptyClick: (position: string, slotIndex: number) => void;
};

function DepthCell({
  position,
  slotIndex,
  rosterPlayerId,
  playerMap,
  onDrop,
  onRemove,
  onReorder,
  depthIds,
  onPlayerClick,
  onEmptyClick
}: DepthCellProps) {
  const player = rosterPlayerId ? playerMap.get(rosterPlayerId) : undefined;
  const itemId = rosterPlayerId ?? `empty-${position}-${slotIndex}`;

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [ItemTypes.DEPTH_PLAYER, ItemTypes.BENCH_PLAYER],
    drop: (item: DragItem) => {
      if (
        item.source === 'depth' &&
        item.fromPosition === position &&
        item.fromSlotIndex === slotIndex
      ) {
        return;
      }
      onDrop(position, slotIndex, item);
    },
    canDrop: () => true,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.DEPTH_PLAYER,
    item: () =>
      rosterPlayerId
        ? {
            rosterPlayerId,
            source: 'depth',
            fromPosition: position,
            fromSlotIndex: slotIndex
          }
        : undefined,
    canDrag: !!rosterPlayerId,
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  });

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (player) onRemove(position, slotIndex);
  };

  const warningLevel = player ? getPositionWarningLevel(player, position) : 'primary';
  const warningConfig = {
    primary: null,
    secondary: { color: 'blue.400', title: 'Secondary position' },
  tertiary: { color: 'yellow.300', title: 'Tertiary position' },
    out: { color: 'red.400', title: 'Out of position' }
  } as const;
  const warning = warningConfig[warningLevel];

  return (
    <Td
      ref={drop}
      fontSize="sm"
      px={2}
      py={1}
      bg={isOver && canDrop ? '#2d3748' : undefined}
      border="1px solid"
      borderColor="gray.700"
      maxW={DEPTH_CELL_WIDTH}
      w={DEPTH_CELL_WIDTH}
      onClick={() => {
        if (!player) {
          onEmptyClick(position, slotIndex);
        }
      }}
    >
      <Flex align="center" justify="space-between" gap={1}>
        <Flex
          ref={player ? drag : undefined}
          flex="1"
          minW={0}
          align="center"
          gap={1}
          opacity={isDragging ? 0.5 : 1}
          cursor={player ? 'move' : 'default'}
          title={player ? 'Drag to reorder' : undefined}
        >
          {player ? (
            <Box
              as="button"
              onClick={(e) => {
                e.stopPropagation();
                onPlayerClick(player);
              }}
              textAlign="left"
              style={{ all: 'unset', cursor: 'pointer' }}
            >
              <Text as="span">{getPlayerName(player)}</Text>
            </Box>
          ) : (
            <Text color="gray.500">—</Text>
          )}
          {warning && (
            <Icon as={FiAlertTriangle} color={warning.color} boxSize={3.5} flexShrink={0} title={warning.title} />
          )}
        </Flex>
        {player && (
          <IconButton
            aria-label="Remove player"
            icon={<CloseIcon boxSize={2.5} />}
            size="xs"
            variant="ghost"
            borderRadius="full"
            minW={5}
            w={5}
            h={5}
            bg="gray.600"
            color="white"
            _hover={{ color: 'white', bg: 'red.500' }}
            onClick={handleRemoveClick}
          />
        )}
      </Flex>
    </Td>
  );
}

type BenchPoolProps = {
  bench: PlayerWithId[];
  lineup: PlayerWithId[];
};

const POSITION_ORDER = ['CA', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH', 'PH', 'PR', 'P', 'SP', 'RP', 'CP'];

// Map API/database position codes to our sort order (e.g. CA -> C, CL -> CP)
const POSITION_NORMALIZE: Record<string, string> = {
  CA: 'CA',
  '1B': '1B',
  '2B': '2B',
  '3B': '3B',
  SS: 'SS',
  LF: 'LF',
  CF: 'CF',
  RF: 'RF',
  DH: 'DH',
  PH: 'PH',
  PR: 'PR',
  P: 'P',
  SP: 'SP',
  RP: 'RP',
  CP: 'CP',
  CL: 'CP',
  IF: '2B',
  OF: 'CF',
  LRF: 'RF'
};

const POSITION_OPTIONS: Record<string, string[]> = {
  CA: ['CA', 'C'],
  '1B': ['1B', 'IF'],
  '2B': ['2B', 'IF', 'SS'],
  '3B': ['3B', 'IF', 'SS'],
  SS: ['SS', 'IF'],
  LF: ['LF', 'OF', 'LRF', 'CF'],
  CF: ['CF', 'OF'],
  RF: ['RF', 'OF', 'LRF', 'CF'],

};

function getPrimaryPositionOrder(player: PlayerWithId): number {
  const positions = getPlayerPositionsArray(player);
  const first = positions[0];
  const raw = (typeof first === 'string' ? first.split(',')[0]?.trim() : first)?.toUpperCase?.() || '';
  const primary = POSITION_NORMALIZE[raw] || raw;
  const idx = POSITION_ORDER.indexOf(primary);
  return idx >= 0 ? idx : POSITION_ORDER.length;
}

const PITCHER_PRIMARY = new Set(['P', 'SP', 'RP', 'CP', 'CL']);

function isPitcherWithNoSecondary(player: PlayerWithId): boolean {
  const positions = getPlayerPositionsArray(player);
  const primary = (positions[0] && typeof positions[0] === 'string' ? positions[0].split(',')[0]?.trim() : positions[0])?.toUpperCase?.() || '';
  const normalizedPrimary = POSITION_NORMALIZE[primary] || primary;
  if (!PITCHER_PRIMARY.has(normalizedPrimary)) return false;
  const hasSecondary = positions.length > 1 || (player.secondaryPosition && player.secondaryPosition.length > 0);
  return !hasSecondary;
}

function BenchPool({ bench, lineup }: BenchPoolProps) {
  const allPlayers = useMemo(() => {
    const fromLineup = lineup.filter((p) => p.roster_player_id && !p.position?.includes('P'));
    const fromBench = bench || [];
    const combined = [...fromLineup, ...fromBench].filter((p) => !isPitcherWithNoSecondary(p));
    return combined.sort((a, b) => {
      const aOrder = getPrimaryPositionOrder(a);
      const bOrder = getPrimaryPositionOrder(b);
      if (aOrder !== bOrder) return aOrder - bOrder;
      const aLast = (a.lastName || '').toLowerCase();
      const bLast = (b.lastName || '').toLowerCase();
      if (aLast !== bLast) return aLast.localeCompare(bLast);
      return (a.firstName || '').toLowerCase().localeCompare((b.firstName || '').toLowerCase());
    });
  }, [lineup, bench]);

  return (
    <Box flex="1" minW="200px">
      <Table size="xs" variant="simple" bg={darkTableBg} color={darkText} borderRadius="md">
        <Thead bg={darkTableBg}>
          <Tr>
            <Th color={darkText} fontSize="sm" px={2} py={1}>PLAYER</Th>
            <Th color={darkText} fontSize="sm" px={2} py={1}>P1</Th>
            <Th color={darkText} fontSize="sm" px={2} py={1}>P2</Th>
            <Th color={darkText} fontSize="sm" px={2} py={1}>P3</Th>
          </Tr>
        </Thead>
        <Tbody>
          {allPlayers.map((p) => (
            <BenchPlayerRow key={p.roster_player_id} player={p} />
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

type PinchHitterRowProps = {
  slotIndex: number;
  entry: PHEntry | { roster_player_id?: number; situation?: PHSituations };
  playerMap: Map<number, PlayerWithId>;
  onDrop: (slotIndex: number, item: DragItem) => void;
  onRemove: (slotIndex: number) => void;
  onSituationChange: (slotIndex: number, situation: PHSituations) => void;
  getPlayerName: (p: PlayerWithId | undefined) => string;
  onPlayerClick: (player: PlayerWithId) => void;
  onEmptyClick: (slotIndex: number) => void;
};

function PinchHitterRow({
  slotIndex,
  entry,
  playerMap,
  onDrop,
  onRemove,
  onSituationChange,
  getPlayerName,
  onPlayerClick,
  onEmptyClick
}: PinchHitterRowProps) {
  const rosterPlayerId = entry?.roster_player_id;
  const situation = (entry?.situation || 'unset') as PHSituations;
  const situationLabel = PH_SITUATIONS.find((s) => s.key === situation)?.label || 'Select…';
  const player = rosterPlayerId ? playerMap.get(rosterPlayerId) : undefined;

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [ItemTypes.DEPTH_PLAYER, ItemTypes.BENCH_PLAYER],
    drop: (item: DragItem) => {
      onDrop(slotIndex, item);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.DEPTH_PLAYER,
    item: () =>
      rosterPlayerId
        ? {
            rosterPlayerId,
            source: 'depth',
            fromPosition: 'PH',
            fromSlotIndex: slotIndex
          }
        : undefined,
    canDrag: !!rosterPlayerId,
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  });

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove(slotIndex);
  };

  return (
    <Tr>
      <Td
        ref={drop}
        fontSize="sm"
        px={2}
        py={1}
        bg={isOver && canDrop ? '#2d3748' : undefined}
        border="1px solid"
        borderColor="gray.700"
        onClick={() => {
          if (!player) {
            onEmptyClick(slotIndex);
          }
        }}
      >
        <Flex align="center" justify="space-between" gap={1}>
          <Flex
            ref={player ? drag : undefined}
            flex="1"
            minW={0}
            align="center"
            gap={1}
            opacity={isDragging ? 0.5 : 1}
            cursor={player ? 'move' : 'default'}
          >
            {player ? (
              <Box
                as="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onPlayerClick(player);
                }}
                textAlign="left"
                style={{ all: 'unset', cursor: 'pointer' }}
              >
                <Text as="span">{getPlayerName(player)}</Text>
              </Box>
            ) : (
              <Text color="gray.500">—</Text>
            )}
          </Flex>
          {player && (
            <IconButton
              aria-label="Remove player"
              icon={<CloseIcon boxSize={2.5} />}
              size="xs"
              variant="ghost"
              borderRadius="full"
              minW={5}
              w={5}
              h={5}
              bg="gray.600"
              color="white"
              _hover={{ color: 'white', bg: 'red.500' }}
              onClick={handleRemoveClick}
            />
          )}
        </Flex>
      </Td>
      <Td fontSize="sm" px={2} py={1}>
        {player && (
          <Menu>
            <MenuButton
              as={Box}
              px={3}
              py={1}
              borderRadius="full"
              fontSize="xs"
              fontWeight="medium"
              cursor="pointer"
              display="inline-flex"
              alignItems="center"
              gap={1}
              bg={PH_PILL_COLORS[situation]}
              color="white"
              _hover={{ opacity: 0.9 }}
            >
              {situation === 'unset' ? 'Select…' : situationLabel}
              <ChevronDownIcon boxSize={3} />
            </MenuButton>
            <MenuList bg={darkTableBg} borderColor="gray.600">
              {PH_SITUATIONS.map(({ key, label }) => (
                <MenuItem
                  key={key}
                  onClick={() => onSituationChange(slotIndex, key)}
                  bg={key === situation ? 'whiteAlpha.200' : undefined}
                >
                  {label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        )}
      </Td>
    </Tr>
  );
}

function BenchPlayerRow({ player }: { player: PlayerWithId }) {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.BENCH_PLAYER,
    item: () => ({
      rosterPlayerId: player.roster_player_id!,
      source: 'bench'
    }),
    canDrag: !!player.roster_player_id,
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  });

  const primary = player.primaryPosition || [];
  const secondary = player.secondaryPosition || [];
  const tertiary = player.tertiaryPosition || [];

  return (
    <Tr
      ref={drag}
      opacity={isDragging ? 0.5 : 1}
      cursor="move"
      _hover={{ bg: 'gray.700' }}
    >
      <Td fontSize="sm" px={2} py={1}>
        {getPlayerName(player)}
      </Td>
      <Td fontSize="sm" px={2} py={1} color={darkSecondary}>
        {primary.length ? primary.join(', ') : '—'}
      </Td>
      <Td fontSize="sm" px={2} py={1} color={darkSecondary}>
        {secondary.length ? secondary.join(', ') : '—'}
      </Td>
      <Td fontSize="sm" px={2} py={1} color={darkSecondary}>
        {tertiary.length ? tertiary.join(', ') : '—'}
      </Td>
    </Tr>
  );
}

interface DepthChartProps {
  teamId?: string;
}

const DepthChart: React.FC<DepthChartProps> = ({ teamId: propTeamId }) => {
  const { myTeam } = useUserContext();
  const teamId = propTeamId || myTeam?.toString() || '';
  const { lineup, bench, isLoading: isLineupLoading } = useTeamLineup(teamId, 'DH');
  const {
    depthChart,
    isLoading: isDepthLoading,
    isUpdating,
    updateDepthChart,
    refreshDepthChart
  } = useDepthChart(teamId, 'DH');

  const [localChart, setLocalChart] = useState<DepthChartType | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerWithId | null>(null);
  const [slotSelect, setSlotSelect] = useState<{
    position: string;
    slotIndex: number;
  } | null>(null);

  useEffect(() => {
    if (depthChart) {
      let phArr: PHEntry[] = [];
      const ph = depthChart.PH;
      if (Array.isArray(ph)) {
        phArr = ph.filter((e): e is PHEntry => e && e.roster_player_id != null);
      } else if (ph && typeof ph === 'object') {
        const old = ph as { any?: number[]; baserunner?: number[]; rbi?: number[]; last?: number[] };
        ['any', 'baserunner', 'rbi', 'last'].forEach((sit) => {
          (old[sit as keyof typeof old] || []).forEach((id) => {
            if (id != null) phArr.push({ roster_player_id: id, situation: sit as PHSituations });
          });
        });
      }
      setLocalChart({ ...depthChart, PH: phArr });
    }
  }, [depthChart]);

  const playerMap = useMemo(() => {
    const map = new Map<number, PlayerWithId>();
    const add = (p: PlayerWithId) => {
      if (p.roster_player_id) map.set(p.roster_player_id, p);
    };
    (lineup || []).forEach(add);
    (bench || []).forEach(add);
    return map;
  }, [lineup, bench]);

  const debouncedUpdate = useDebouncedCallback(
    (chart: Omit<DepthChartType, 'hasData'>) => {
      updateDepthChart(chart);
    },
    400
  );

  const applyChange = useCallback(
    (newChart: DepthChartType) => {
      setLocalChart(newChart);
      const { hasData, ...rest } = newChart;
      const toSend = {
        ...rest,
        PH: (Array.isArray(rest.PH) ? rest.PH : []).map((e) => ({
          roster_player_id: e.roster_player_id,
          situation: e.situation === 'unset' ? undefined : e.situation
        }))
      };
      debouncedUpdate(toSend);
    },
    [debouncedUpdate]
  );

  const handleDrop = useCallback(
    (position: string, slotIndex: number, item: DragItem) => {
      const rosterPlayerId = item.rosterPlayerId;
      if (!localChart) return;
      const next: DepthChartType = {
        ...localChart,
        PH: [...(localChart.PH || [])]
      };

      // If dragging from an existing depth/PH slot, remove that single occurrence first (move)
      if (item.source === 'depth') {
        if (item.fromPosition === 'PH') {
          const phArr = [...(next.PH || [])];
          if (phArr[item.fromSlotIndex]?.roster_player_id === rosterPlayerId) {
            phArr.splice(item.fromSlotIndex, 1);
            next.PH = phArr;
          }
        } else {
          const fromArr = ([...(next as any)[item.fromPosition]] as number[]) || [];
          if (fromArr[item.fromSlotIndex] === rosterPlayerId) {
            fromArr.splice(item.fromSlotIndex, 1);
            (next as any)[item.fromPosition] = fromArr;
          }
        }
      }

      // Prevent duplicates within the same position row
      const existingInRow = ((next as any)[position] as number[] | undefined) || [];
      if (existingInRow.includes(rosterPlayerId)) {
        return;
      }

      const targetArr = [...(next[position as keyof DepthChartType] as number[] || [])];
      targetArr.splice(Math.min(slotIndex, targetArr.length), 0, rosterPlayerId);
      next[position as keyof DepthChartType] = targetArr;
      next.hasData = true;
      applyChange(next);
    },
    [localChart, applyChange]
  );

  const handlePHDrop = useCallback(
    (slotIndex: number, item: DragItem) => {
      const rosterPlayerId = item.rosterPlayerId;
      if (!localChart) return;
      const next: DepthChartType = {
        ...localChart,
        PH: [...(localChart.PH || [])]
      };

      let movingEntry: PHEntry | null = null;

      // If moving from an existing slot, remove that occurrence first
      if (item.source === 'depth') {
        if (item.fromPosition === 'PH') {
          const phArr = [...(next.PH || [])];
          if (phArr[item.fromSlotIndex]?.roster_player_id === rosterPlayerId) {
            [movingEntry] = phArr.splice(item.fromSlotIndex, 1);
            next.PH = phArr;
          }
        } else {
          const fromArr = ([...(next as any)[item.fromPosition]] as number[]) || [];
          if (fromArr[item.fromSlotIndex] === rosterPlayerId) {
            fromArr.splice(item.fromSlotIndex, 1);
            (next as any)[item.fromPosition] = fromArr;
          }
        }
      }

      // Prevent duplicate entries in PH list
      const phAfterMove = [...(next.PH || [])];
      if (phAfterMove.some((e) => e.roster_player_id === rosterPlayerId)) {
        return;
      }

      const ph = phAfterMove;
      const existingEntry = ph.find((e) => e.roster_player_id === rosterPlayerId);
      const situation =
        movingEntry?.situation ||
        (existingEntry?.situation && existingEntry.situation !== 'unset' ? existingEntry.situation : 'unset');
      const entry: PHEntry = movingEntry || { roster_player_id: rosterPlayerId, situation };

      const insertAt = Math.min(slotIndex, ph.length);
      ph.splice(insertAt, 0, entry);
      next.PH = ph;
      next.hasData = true;
      applyChange(next);
    },
    [localChart, applyChange]
  );

  const handlePHRemove = useCallback(
    (slotIndex: number) => {
      if (!localChart) return;
      const next = { ...localChart, PH: [...(localChart.PH || [])] };
      next.PH.splice(slotIndex, 1);
      const hasOtherData = DEPTH_POSITIONS.some((pos) => ((next as any)[pos] as number[])?.length > 0);
      next.hasData = (next.PH?.length || 0) > 0 || hasOtherData;
      applyChange(next);
    },
    [localChart, applyChange]
  );

  const handlePlayerClick = useCallback(
    (player: PlayerWithId) => {
      setSelectedPlayer(player);
    },
    []
  );

  const selectablePlayers = useMemo(() => {
    const fromLineup = (lineup || []).filter(
      (p) => p.roster_player_id && !p.position?.includes('P')
    ) as PlayerWithId[];
    const fromBench = (bench || []) as PlayerWithId[];
    const combined = [...fromLineup, ...fromBench].filter(
      (p) => !isPitcherWithNoSecondary(p)
    );
    return combined.sort((a, b) => {
      const aOrder = getPrimaryPositionOrder(a);
      const bOrder = getPrimaryPositionOrder(b);
      if (aOrder !== bOrder) return aOrder - bOrder;
      const aLast = (a.lastName || '').toLowerCase();
      const bLast = (b.lastName || '').toLowerCase();
      if (aLast !== bLast) return aLast.localeCompare(bLast);
      return (a.firstName || '').toLowerCase().localeCompare((b.firstName || '').toLowerCase());
    });
  }, [lineup, bench]);

  const handleEmptySlotClick = useCallback((position: string, slotIndex: number) => {
    setSlotSelect({ position, slotIndex });
  }, []);

  const orderedSelectableForContext = useMemo(() => {
    if (!slotSelect) return [];

    const chartForFilter = localChart || depthChart;
    if (!chartForFilter) return [];

    let base = selectablePlayers;

    // Exclude players already in this row (or PH list)
    if (slotSelect.position === 'PH') {
      const usedIds = (Array.isArray(chartForFilter.PH) ? chartForFilter.PH : []).map(
        (e) => e.roster_player_id
      );
      base = base.filter((p) => !usedIds.includes(p.roster_player_id!));
      return base;
    } else {
      const rowIds =
        ((chartForFilter as any)[slotSelect.position] as number[] | undefined) || [];
      base = base.filter((p) => !rowIds.includes(p.roster_player_id!));
    }

    const options = POSITION_OPTIONS[slotSelect.position] || [slotSelect.position];
    const rank = (p: PlayerWithId): number => {
      const hasPrimary = p.primaryPosition?.some((pos) => options.includes(pos));
      if (hasPrimary) return 0;
      const hasSecondary = p.secondaryPosition?.some((pos) => options.includes(pos));
      if (hasSecondary) return 1;
      const hasTertiary = p.tertiaryPosition?.some((pos) => options.includes(pos));
      if (hasTertiary) return 2;
      return 3;
    };
    const withIndex = base.map((p, idx) => ({ p, idx }));
    withIndex.sort((a, b) => {
      const ra = rank(a.p);
      const rb = rank(b.p);
      if (ra !== rb) return ra - rb;
      return a.idx - b.idx;
    });
    return withIndex.map((x) => x.p);
  }, [slotSelect, selectablePlayers]);

  const handlePHSituationChange = useCallback(
    (slotIndex: number, situation: PHSituations) => {
      if (!localChart) return;
      const next = { ...localChart, PH: [...(localChart.PH || [])] };
      if (next.PH[slotIndex]) {
        next.PH[slotIndex] = { ...next.PH[slotIndex], situation };
        next.hasData = true;
        applyChange(next);
      }
    },
    [localChart, applyChange]
  );

  const handleRemove = useCallback(
    (position: string, slotIndex: number) => {
      if (!localChart) return;
      const posArr = [...(localChart[position as keyof DepthChartType] || [])];
      if (typeof posArr[0] === 'number') {
        const ids = posArr as number[];
        ids.splice(slotIndex, 1);
        const next = { ...localChart, [position]: ids };
        const hasAny = Object.values(localChart).some((v) => {
          if (Array.isArray(v)) return v.length > 0;
          if (v && typeof v === 'object' && !Array.isArray(v)) {
            return Object.values(v).some((a) => Array.isArray(a) && a.length > 0);
          }
          return false;
        });
        next.hasData = hasAny;
        applyChange(next);
      }
    },
    [localChart, applyChange]
  );

  const handleReorder = useCallback(
    (position: string, fromIdx: number, toIdx: number) => {
      if (!localChart) return;
      const posArr = [...(localChart[position as keyof DepthChartType] || [])];
      if (typeof posArr[0] === 'number') {
        const ids = posArr as number[];
        const [removed] = ids.splice(fromIdx, 1);
        ids.splice(toIdx, 0, removed);
        const next = { ...localChart, [position]: ids };
        next.hasData = true;
        applyChange(next);
      }
    },
    [localChart, applyChange]
  );

  if (!teamId) {
    return (
      <Box p={6} bg={darkBg} borderRadius="lg" color={darkText}>
        <Text color={darkSecondary}>Select your team to manage the depth chart.</Text>
      </Box>
    );
  }

  if (isLineupLoading || isDepthLoading) {
    return (
      <Center py={12}>
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  const chart = localChart || depthChart;

  return (
    <Box p={6} bg={darkBg} borderRadius="lg" color={darkText}>
      <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
        <Flex align="center" gap={3} flex="1" minW={0}>
          <Text fontSize="lg" fontWeight="bold" whiteSpace="nowrap">
            Depth Chart
          </Text>
          <Text
            fontSize="xs"
            color="yellow.300"
            display={{ base: 'none', lg: 'inline' }}
          >
            Drag from the right or within the chart. Click a name to remove from that slot.
          </Text>
        </Flex>
        <Flex align="center" gap={4} justify="flex-end" flexShrink={0}>
          <Flex align="center" gap={1}>
            <Icon as={FiAlertTriangle} boxSize={3} color="blue.400" />
            <Text fontSize="xs" color={darkSecondary}>Secondary</Text>
          </Flex>
          <Flex align="center" gap={1}>
            <Icon as={FiAlertTriangle} boxSize={3} color="yellow.300" />
            <Text fontSize="xs" color={darkSecondary}>Tertiary</Text>
          </Flex>
          <Flex align="center" gap={1}>
            <Icon as={FiAlertTriangle} boxSize={3} color="red.400" />
            <Text fontSize="xs" color={darkSecondary}>Out of Position</Text>
          </Flex>
          {isUpdating && (
            <Text fontSize="sm" color={darkSecondary}>
              Saving…
            </Text>
          )}
        </Flex>
      </Flex>

      <Flex
        gap={8}
        align="flex-start"
        flexDirection={{ base: 'column', lg: 'row' }}
      >
        <Box flex="2" minW={0}>
          <Table size="sm" variant="simple" colorScheme="gray" bg={darkTableBg} borderRadius="md">
            <Thead>
              <Tr>
                <Th bg="gray.800" color={darkText} w="60px">
                  POS
                </Th>
                {DEPTH_COLUMNS.map((col) => (
                  <Th key={col} bg="gray.800" color={darkText} textAlign="center" w={DEPTH_CELL_WIDTH}>
                          {col}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {DEPTH_POSITIONS.map((pos) => (
                <Tr key={pos}>
                  <Td fontWeight="bold" bg="blue.900" color={darkText} borderBottom="3px solid" borderColor="blue.700">
                    {pos}
                  </Td>
                  {[0, 1, 2, 3].map((slotIdx) => {
                    const ids = (chart[pos as keyof DepthChartType] as number[]) || [];
                    const rid = ids[slotIdx];
                    return (
                      <DepthCell
                        key={`${pos}-${slotIdx}`}
                        position={pos}
                        slotIndex={slotIdx}
                        rosterPlayerId={rid}
                        playerMap={playerMap}
                        onDrop={handleDrop}
                        onRemove={handleRemove}
                        onReorder={handleReorder}
                        depthIds={ids}
                        onPlayerClick={handlePlayerClick}
                    onEmptyClick={handleEmptySlotClick}
                      />
                    );
                  })}
                </Tr>
              ))}
            </Tbody>
          </Table>

          <Flex align="center" gap={3} mt={4} mb={2}>
            <Text fontWeight="bold">
              Pinch Hitters
            </Text>
            <Text
              fontSize="xs"
              color="yellow.300"
              display={{ base: 'none', lg: 'inline' }}
            >
              Drag players and put in order of pinch hitting preference
            </Text>
          </Flex>
          <Table size="sm" variant="simple" colorScheme="gray" bg={darkTableBg} borderRadius="md">
            <Thead>
              <Tr>
                <Th bg="gray.800" color={darkText}>Player</Th>
                <Th bg="gray.800" color={darkText}>Situation</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(() => {
                const ph = Array.isArray(chart.PH) ? chart.PH : [];
                const displayRows: (PHEntry | { roster_player_id?: number; situation?: PHSituations })[] = [...ph];
                while (displayRows.length < 5) displayRows.push({});
                if ((displayRows[displayRows.length - 1] as PHEntry).roster_player_id) {
                  displayRows.push({});
                }
                return displayRows.map((entry, idx) => (
                  <PinchHitterRow
                    key={idx}
                    slotIndex={idx}
                    entry={entry}
                    playerMap={playerMap}
                    onDrop={handlePHDrop}
                    onRemove={handlePHRemove}
                    onSituationChange={handlePHSituationChange}
                    getPlayerName={getPlayerName}
                    onPlayerClick={handlePlayerClick}
                    onEmptyClick={(slot) => handleEmptySlotClick('PH', slot)}
                  />
                ));
              })()}
            </Tbody>
          </Table>
        </Box>
        <BenchPool bench={bench || []} lineup={lineup || []} />
      </Flex>

      {selectedPlayer && (
        <Modal
          isOpen={!!selectedPlayer}
          onClose={() => setSelectedPlayer(null)}
          isCentered
          size="md"
        >
          <ModalOverlay />
          <ModalContent bg={darkBg} color={darkText}>
            <ModalHeader>
              {getPlayerName(selectedPlayer) || 'Player Details'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <Box mb={3}>
                <Text fontWeight="bold" mb={1}>
                  Primary positions:
                </Text>
                <Text>
                  {(selectedPlayer.primaryPosition && selectedPlayer.primaryPosition.length
                    ? selectedPlayer.primaryPosition
                    : []
                  ).join(', ') || '—'}
                </Text>
              </Box>
              <Box mb={3}>
                <Text fontWeight="bold" mb={1}>
                  Secondary positions:
                </Text>
                <Text>
                  {selectedPlayer.secondaryPosition && selectedPlayer.secondaryPosition.length
                    ? selectedPlayer.secondaryPosition.join(', ')
                    : '—'}
                </Text>
              </Box>
              <Box mb={3}>
                <Text fontWeight="bold" mb={1}>
                  Tertiary positions:
                </Text>
                <Text>
                  {selectedPlayer.tertiaryPosition && selectedPlayer.tertiaryPosition.length
                    ? selectedPlayer.tertiaryPosition.join(', ')
                    : '—'}
                </Text>
              </Box>

              <Divider my={3} />

              <Box mt={3}>
               
                <Text fontSize="sm" color={darkSecondary}>
                  {selectedPlayer.roster_player_id ?? '—'} - {teamId || '—'} -{' '}
                  {'id' in selectedPlayer && (selectedPlayer as any).id
                    ? (selectedPlayer as any).id
                    : '—'}
                </Text>
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}

      {slotSelect && (
        <Modal
          isOpen={!!slotSelect}
          onClose={() => setSlotSelect(null)}
          isCentered
          size="md"
        >
          <ModalOverlay />
          <ModalContent bg={darkBg} color={darkText} maxH="75vh">
            <ModalHeader>
              {slotSelect.position === 'PH'
                ? `Select Pinch Hitter #${slotSelect.slotIndex + 1}`
                : `Select depth #${slotSelect.slotIndex + 1} for ${slotSelect.position}`}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={4}>
              {orderedSelectableForContext.length === 0 ? (
                <Text fontSize="sm" color={darkSecondary}>
                  No available players.
                </Text>
              ) : (
                <Box maxH="60vh" overflowY="auto">
                  <Table size="sm" variant="simple" bg={darkTableBg}>
                    <Thead>
                      <Tr>
                        <Th color={darkText}>Player</Th>
                        <Th color={darkText}>P1</Th>
                        <Th color={darkText}>P2</Th>
                        <Th color={darkText}>P3</Th>
                        <Th />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {orderedSelectableForContext.map((p) => {
                        const isPrimaryMatch =
                          slotSelect?.position !== 'PH' &&
                          !!p.primaryPosition &&
                          !!POSITION_OPTIONS[slotSelect.position] &&
                          p.primaryPosition.some((pos) =>
                            POSITION_OPTIONS[slotSelect.position].includes(pos)
                          );
                        return (
                          <Tr key={p.roster_player_id}>
                            <Td fontWeight={isPrimaryMatch ? 'bold' : 'normal'}>
                              {getPlayerName(p)}
                            </Td>
                            <Td
                              fontSize="xs"
                              color={isPrimaryMatch ? darkText : darkSecondary}
                              fontWeight={isPrimaryMatch ? 'bold' : 'normal'}
                            >
                              <Text
                                as="span"
                                fontWeight={isPrimaryMatch ? 'bold' : 'normal'}
                              >
                                {p.primaryPosition && p.primaryPosition.length
                                  ? p.primaryPosition.join(', ')
                                  : '—'}
                              </Text>
                            </Td>
                            <Td fontSize="xs" color={darkSecondary}>
                              {p.secondaryPosition && p.secondaryPosition.length
                                ? p.secondaryPosition.join(', ')
                                : '—'}
                            </Td>
                            <Td fontSize="xs" color={darkSecondary}>
                              {p.tertiaryPosition && p.tertiaryPosition.length
                                ? p.tertiaryPosition.join(', ')
                                : '—'}
                            </Td>
                            <Td textAlign="right">
                            <Button
                              size="xs"
                              colorScheme="blue"
                              onClick={() => {
                                if (!p.roster_player_id) return;
                                const item: DragItem = {
                                  rosterPlayerId: p.roster_player_id,
                                  source: 'bench'
                                } as BenchDragItem;
                                if (slotSelect.position === 'PH') {
                                  handlePHDrop(slotSelect.slotIndex, item);
                                } else {
                                  handleDrop(slotSelect.position, slotSelect.slotIndex, item);
                                }
                                setSlotSelect(null);
                              }}
                            >
                              Select
                            </Button>
                          </Td>
                        </Tr>
                      );
                    })}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default DepthChart;
